import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { GetDataPipelineUploadSignedUrlDocumentGQLType } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { RDFV } from 'packages/dataPipeline/types/dataPipelineTypes';
import { presignedPostUpload } from 'packages/files/ui/utils';

export type UploadCallback = (params: {
  loaded: number;
  total: number;
}) => void;

export type DirectUpload = ({
  file,
  onEventCallback,
}: {
  file: File;
  onEventCallback: UploadCallback;
}) => Promise<string>;

export const useDirectUpload = ({
  artifactId,
  borrowerOrganizationId,
  rdfv,
}: {
  artifactId: string;
  borrowerOrganizationId: string;
  rdfv: RDFV;
}): { directUpload: DirectUpload } => {
  const { reportingDate, fileVersion } = rdfv;
  const client = useApolloClient();
  const directUpload = useCallback(
    async ({
      file,
      onEventCallback,
    }: {
      file: File;
      onEventCallback: UploadCallback;
    }): Promise<string> => {
      // The helper throws and captures a sentry exception if an error occurs
      const { queryResponse } = await presignedPostUpload<
        DataPipeline.UploadDestinationRequest,
        { artifactInstanceId: string }
      >({
        client,
        file,
        query: GetDataPipelineUploadSignedUrlDocumentGQLType,
        queryVariables: {
          organizationId: borrowerOrganizationId,
          artifactId,
          reportingDate,
          fileVersion,
        },
        progressEventCallback: ({ loaded, total }) => {
          onEventCallback({ loaded, total });
        },
      });

      finleyAssert(
        queryResponse.artifactInstanceId,
        'BBPL artifact instance id missing.',
      );

      return queryResponse.artifactInstanceId;
    },
    [artifactId, borrowerOrganizationId, client, fileVersion, reportingDate],
  );

  return {
    directUpload,
  };
};
